import { Alert, AlertTitle, Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import { isNotNullOrUndefined } from '@vy/utils';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { PasswordInput } from '../../LoginPage/login-page-components/PasswordInput';
import type { NewRegisterForm } from '../register-page-types/new-register-form-schema';
import type { RegisterFormInput } from '../register-page-types/register-form-input-schema';

interface RegisterPageFormProps {
  errorMessage?: string;
}

export const RegisterPageForm: React.FC<RegisterPageFormProps> = ({ errorMessage }) => {
  const {
    register,
    formState: { errors, isSubmitting, isSubmitSuccessful, isSubmitted },
  } = useFormContext<RegisterFormInput, unknown, NewRegisterForm>();

  const usernameRegister = register('username');
  const passwordRegister = register('password');
  const confirmPasswordRegister = register('confirmPassword');

  return (
    <div className="w-[456px] p-8 flex flex-col gap-8">
      <VyIcon icon="LogoBlue" className="text-transparent" />
      <Box>
        <Typography variant="h5" sx={{
          fontWeight: 300
        }}>
          Welcome to Vayu!
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 300,
            color: "#1B3D5599",
            fontSize: 14
          }}>
          Please complete setup to continue
        </Typography>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          width: '100%',
        }}
      >
        <TextField
          label="Email Address"
          variant="filled"
          size="medium"
          fullWidth
          error={isNotNullOrUndefined(errors.username)}
          helperText={errors.username?.message}
          disabled
          {...usernameRegister}
        />

        <PasswordInput
          error={isNotNullOrUndefined(errors.password)}
          helperText={errors.password?.message}
          {...passwordRegister}
        />

        <PasswordInput
          label="Confirm Password"
          error={isNotNullOrUndefined(errors.confirmPassword)}
          helperText={errors.confirmPassword?.message}
          {...confirmPasswordRegister}
        />
      </Box>
      <Button
        variant="contained"
        fullWidth
        type="submit"
        size="large"
        endIcon={!isSubmitting && <VyIcon icon="ArrowSmallRight" />}
      >
        {isSubmitting && !isSubmitSuccessful ? <CircularProgress size={24} color="inherit" /> : 'Register'}
      </Button>
      {isSubmitted && !isSubmitSuccessful && (
        <Alert severity="error">
          <AlertTitle>Registration failed!</AlertTitle>
          {errorMessage ?? 'Please ensure username and password are valid.'}
        </Alert>
      )}
    </div>
  );
};
